/**
 * Notifications — §3, the O-1 control.
 *
 * This is the service that makes *"no missed dates"* true rather than
 * aspirational, and the idempotency key below is the detail the whole control
 * rests on.
 */
import { z } from 'zod';
import { ContractKeySchema, IsoDateSchema, IsoTimestampUtcSchema, OpaqueIdSchema, UpnSchema, } from '../primitives.js';
import { NotificationRecipientRuleSchema, NotificationTierSchema, NotificationWatchTypeSchema, } from '../vocabulary.js';
/**
 * The idempotency key of a sent notification: **`(rule, contract,
 * occurrence_date, tier)`** — §3.3, `notification_sent`'s
 * `UNIQUE (rule_id, contract_key, occurrence_date, tier)`.
 *
 * This tuple, not a surrogate id, is how a notification is addressed across this
 * contract. It has to be: a daily timer re-queries the same window every morning,
 * and without the key each alert re-sends. Then people mute the channel, and
 * **O-1 fails silently while every dashboard stays green** (CLAUDE.md rule 15).
 *
 * Addressing an acknowledgement by this key rather than by `sent_id` makes the
 * acknowledgement itself idempotent — a double-click, a retried request or a
 * duplicated Teams action card all resolve to the same row.
 */
export const NotificationOccurrenceKeySchema = z
    .object({
    /** Identifies the rule. `notification_rule` is reference data, not a resource. */
    rule_id: OpaqueIdSchema,
    contract_key: ContractKeySchema,
    /** The date being warned *about* — not the date the alert was sent. */
    occurrence_date: IsoDateSchema,
    tier: NotificationTierSchema,
})
    .strict();
/**
 * A notification that was sent and may need acknowledging.
 *
 * Acknowledgement is a **first-class action** (§3.2): recorded with actor and
 * timestamp, and it stops escalation. A single email is not a control.
 */
export const NotificationSchema = z.object({
    occurrence: NotificationOccurrenceKeySchema,
    watch_type: NotificationWatchTypeSchema,
    /** Cumulative — tier 2 adds the Practice Director to the Client Success owner. */
    recipient_rules: z.array(NotificationRecipientRuleSchema).min(1),
    /** Resolved server-side from Entra scope groups (§3.5), never caller-supplied. */
    recipients: z.array(z.string().min(1).max(200)),
    sent_utc: IsoTimestampUtcSchema,
    /** `null` for past-due, which re-fires daily rather than at a fixed offset. */
    days_ahead: z.number().int().min(0).nullable(),
    acknowledged_by_upn: UpnSchema.nullable(),
    acknowledged_utc: IsoTimestampUtcSchema.nullable(),
    /** For display: what the alert is about. */
    contract_summary_line: z.string().min(1).max(400),
});
//# sourceMappingURL=notification.js.map